import React from 'react';
import SEO from './SEO';
import JsonLd from './JsonLd';
import Breadcrumbs from './Breadcrumbs';
import AuthorBox from './AuthorBox';
import { FIRMA, ADRES_PELNY, CENNIK, cenaTekst, DIAGNOZA, DOJAZD, MAPS_EMBED, MAPS_LINK, MAPS_NAV } from '../data/firma';
import { OPINIE } from '../data/opinie';
import { DIAGNOZA_SERVICE, breadcrumbSchema, faqSchema } from '../data/schema';
import page from '../css/modules/ContentPage.module.css';
import s from '../css/modules/Landing.module.css';

/**
 * Glowna strona komercyjna dla zapytan o diagnoze sensoryczna w Olsztynie
 * (spec sekcja 1). Wszystkie warianty frazy - "diagnoza SI", "badanie
 * integracji sensorycznej", "diagnoza integracji sensorycznej" - obsluguje
 * TA strona; nie tworzymy pod nie osobnych adresow.
 */

const PATH = '/diagnoza-sensoryczna-olsztyn/';

const CRUMBS = [
  { name: 'Strona główna', path: '/' },
  { name: 'Diagnoza sensoryczna Olsztyn', path: PATH },
];

const OBJAWY = [
  'źle znosi hałas, tłum, metki przy ubraniach, mycie głowy albo obcinanie paznokci',
  'unika huśtania się, wspinania i zabaw ruchowych - albo przeciwnie, ciągle biega, skacze i zderza się z otoczeniem',
  'często się potyka, wpada na przedmioty, wylewa napoje, ma nietypowo mocny lub słaby chwyt',
  'ma trudność z rysowaniem, cięciem nożyczkami, zapinaniem guzików, wiązaniem butów',
  'je bardzo wąski zestaw potraw, krztusi się nowymi konsystencjami, unika określonych zapachów',
  'trudno mu usiedzieć na zajęciach, szybko się rozprasza albo zamyka w sobie w grupie',
  'źle znosi zmiany, dłużej się wycisza, ma gwałtowne reakcje na pozornie drobne bodźce',
  'niechętnie idzie do przedszkola lub szkoły i szybciej niż rówieśnicy męczy się w ciągu dnia',
];

const KROKI = [
  {
    h: 'Kwestionariusz i rozmowa telefoniczna',
    p: 'Przed spotkaniem rodzic (lub opiekun) wypełnia kwestionariusz sensomotoryczny online - dziecko go nie wypełnia. Dzięki temu przychodzisz na wizytę z gotowym opisem trudności, a ja mam czas przygotować się do rozmowy.',
  },
  {
    h: 'Wywiad z rodzicem i badanie dziecka - jedna wizyta',
    p: `${DIAGNOZA.wywiad} ${DIAGNOZA.narzedzia} Dla dziecka wygląda to jak zestaw zadań ruchowych i zabaw, a nie jak badanie.`,
  },
  {
    h: 'Omówienie wyników i pisemna opinia',
    p: `${DIAGNOZA.czasOpinii} Podczas omówienia tłumaczę wyniki prostym językiem i pokazuję, co z nich wynika dla codziennego dnia dziecka - w domu, w przedszkolu i w szkole.`,
  },
];

const FAQ = [
  {
    q: 'W jakim wieku dziecka warto wykonać diagnozę integracji sensorycznej?',
    a: DIAGNOZA.wiek,
  },
  {
    q: 'Ile spotkań obejmuje diagnoza?',
    a: [DIAGNOZA.spotkaniaTekst, DIAGNOZA.czasSpotkania].filter(Boolean).join(' '),
  },
  {
    q: 'Kto wypełnia kwestionariusz sensomotoryczny przed diagnozą?',
    a: 'Kwestionariusz wypełnia rodzic lub opiekun dziecka - nie samo dziecko. Pytania dotyczą codziennych obserwacji: reakcji na dotyk, dźwięki, ruch, jedzenie oraz radzenia sobie z czynnościami dnia codziennego. Formularz jest dostępny online pod adresem sensorini.pl/kwestionariusz-sensomotoryczny/, wypełnienie zajmuje około 15 minut, a odpowiedzi trafiają bezpośrednio do mnie i służą do przygotowania wywiadu.',
  },
  {
    q: 'Czy rodzic jest obecny podczas badania dziecka?',
    a: DIAGNOZA.obecnoscRodzica,
  },
  {
    q: 'Co trzeba zabrać na diagnozę?',
    a: DIAGNOZA.coZabrac,
  },
  {
    q: 'Czy po diagnozie zawsze konieczna jest terapia?',
    a: 'Nie. Część trudności wynika z etapu rozwoju, przemęczenia albo z przyczyn, które wymagają konsultacji u innego specjalisty. Jeśli wyniki nie wskazują na zaburzenia przetwarzania sensorycznego, mówię o tym wprost i przekazuję zalecenia bez proponowania terapii.',
  },
  {
    q: 'Czy diagnoza SI zastępuje diagnozę psychologiczną lub neurologiczną?',
    a: 'Nie. Diagnoza integracji sensorycznej opisuje sposób, w jaki dziecko odbiera i porządkuje bodźce zmysłowe. Nie stawia rozpoznania medycznego. Jeśli obraz trudności na to wskazuje, kieruję rodzica do psychologa, neurologa, logopedy, fizjoterapeuty lub okulisty.',
  },
  {
    q: 'Czy do gabinetu da się dojechać komunikacją miejską i czy jest parking?',
    a: `${DOJAZD.komunikacja} ${DOJAZD.parking}`,
  },
];

const CtaRow = ({ text, location }) => (
  <div className={s.ctaRow}>
    <p className={s.ctaRowText}>{text}</p>
    <a
      href="/kwestionariusz-sensomotoryczny/"
      className={page.btnPrimary}
      data-cta="booking"
      data-cta-location={location}
    >
      Wypełnij kwestionariusz o dziecku
    </a>
    <a href={`tel:${FIRMA.telefonE164}`} className={page.btnOutline}>
      Zadzwoń
    </a>
  </div>
);

export default function DiagnozaOlsztyn() {
  return (
    <div className={page.page}>
      <SEO
        title="Diagnoza sensoryczna Olsztyn – diagnoza SI dziecka | Sensorini"
        description="Diagnoza integracji sensorycznej dzieci w Olsztynie: wywiad, obserwacja, testy, pisemna opinia i zalecenia. Sensorini, ul. Partyzantów 31/5."
        image="/image/diagnoza-integracji-sensorycznej-olsztyn.webp"
        pathname={PATH}
        type="website"
      />
      <JsonLd data={[DIAGNOZA_SERVICE, breadcrumbSchema(CRUMBS), faqSchema(FAQ)]} />

      {/* ---------------- HERO ---------------- */}
      <header className={page.hero} id="hero">
        <span className={`${page.blob} ${page.blobA}`} aria-hidden="true" />
        <span className={`${page.blob} ${page.blobB}`} aria-hidden="true" />
        <div className={page.heroInner}>
          <span className={page.eyebrow}>Gabinet Sensorini • Olsztyn</span>
          <h1 className={page.h1}>Diagnoza integracji sensorycznej dziecka w Olsztynie</h1>
          <p className={page.lead}>
            Pomagam rodzicom zrozumieć, skąd mogą wynikać trudności dziecka z koncentracją, ruchem,
            dotykiem, hałasem, jedzeniem i codziennymi czynnościami.
          </p>

          <div className={s.heroCtas}>
            <a
              href="/kwestionariusz-sensomotoryczny/"
              className={page.btnPrimary}
              data-cta="booking"
              data-cta-location="hero"
            >
              Wypełnij kwestionariusz o dziecku
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={page.btnGhost}>
              Zadzwoń: {FIRMA.telefon}
            </a>
          </div>

          <address className={s.heroAddress}>
            <strong>{FIRMA.nazwaKrotka}</strong>
            <br />
            {FIRMA.ulica}
            <br />
            {FIRMA.kod} {FIRMA.miasto}
          </address>
        </div>
      </header>

      <Breadcrumbs items={CRUMBS} />

      <div className={`${page.wrap} ${page.wrapWide}`}>
        {/* ---------------- KIEDY ROZWAZYC ---------------- */}
        <section className={page.section} id="when-to-consider-diagnosis">
          <h2>Kiedy warto rozważyć diagnozę?</h2>
          <p>
            Rodzice najczęściej przychodzą, gdy codzienne sytuacje - ubieranie, posiłek, wyjście do
            sklepu, odrabianie lekcji - kosztują dziecko i całą rodzinę nieproporcjonalnie dużo
            wysiłku. Warto umówić badanie, jeśli zauważasz, że dziecko:
          </p>
          <ul className={page.list}>
            {OBJAWY.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
          <p className={page.callout}>
            Pojedynczy objaw z tej listy nie oznacza jeszcze zaburzeń przetwarzania sensorycznego.
            Niektóre zachowania mogą być związane z trudnościami w przetwarzaniu bodźców, ale
            wymagają indywidualnej oceny - i po to właśnie jest diagnoza.
          </p>
          <CtaRow
            text="Rozpoznajesz swoje dziecko w kilku punktach? Umówmy termin i sprawdźmy to spokojnie."
            location="po-objawach"
          />
        </section>

        {/* ---------------- JAK WYGLADA ---------------- */}
        <section className={page.section} id="how-diagnosis-works">
          <h2>Jak wygląda proces diagnozy?</h2>
          <p>
            {[DIAGNOZA.spotkaniaTekst, DIAGNOZA.czasSpotkania].filter(Boolean).join(' ')}
          </p>
          <ol className={s.steps}>
            {KROKI.map((k) => (
              <li key={k.h}>
                <h3>{k.h}</h3>
                <p>{k.p}</p>
              </li>
            ))}
          </ol>
          <p className={page.callout}>
            <strong>Kwestionariusz wypełnia rodzic</strong> (lub opiekun dziecka), samodzielnie i we
            własnym tempie - pytania dotyczą codziennych sytuacji, które rodzic obserwuje w domu,
            przedszkolu i szkole. Wypełnienie zajmuje około 15 minut i nie jest testem dla dziecka.
          </p>
          <CtaRow text="Chcesz zacząć od wywiadu? Zadzwoń albo napisz - ustalimy termin." location="po-procesie" />
        </section>

        {/* ---------------- CO ZAWIERA ---------------- */}
        <section className={page.section} id="what-is-included">
          <h2>Co zawiera pisemna opinia i co dostajesz w cenie?</h2>
          <ul className={page.list}>
            {CENNIK.diagnoza.zawiera.map((z) => (
              <li key={z}>{z}</li>
            ))}
          </ul>

          <h3 style={{ marginTop: '2rem' }}>Z czego składa się samo badanie</h3>
          <ul className={page.list}>
            {DIAGNOZA.narzedziaLista.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>

          <h3 style={{ marginTop: '2rem' }}>Najważniejsze informacje praktyczne</h3>
          <dl className={s.facts}>
            <div className={s.factRow}>
              <dt>Wiek dziecka</dt>
              <dd>{DIAGNOZA.wiek}</dd>
            </div>
            <div className={s.factRow}>
              <dt>Liczba spotkań</dt>
              <dd>{DIAGNOZA.spotkaniaTekst}</dd>
            </div>
            {/* Czas wizyty pokazujemy tylko wtedy, gdy jest potwierdzony w
                firma.js - pusta wartosc znaczy "nie wiemy", a nie "0 minut". */}
            {DIAGNOZA.czasSpotkania && (
              <div className={s.factRow}>
                <dt>Czas wizyty</dt>
                <dd>{DIAGNOZA.czasSpotkania}</dd>
              </div>
            )}
            <div className={s.factRow}>
              <dt>Wywiad z rodzicem</dt>
              <dd>{DIAGNOZA.wywiad}</dd>
            </div>
            <div className={s.factRow}>
              <dt>Narzędzia i próby</dt>
              <dd>{DIAGNOZA.narzedzia}</dd>
            </div>
            <div className={s.factRow}>
              <dt>Pisemna opinia</dt>
              <dd>
                {DIAGNOZA.opinia} {DIAGNOZA.czasOpinii}
              </dd>
            </div>
            <div className={s.factRow}>
              <dt>Co zabrać ze sobą</dt>
              <dd>{DIAGNOZA.coZabrac}</dd>
            </div>
            <div className={s.factRow}>
              <dt>Obecność rodzica</dt>
              <dd>{DIAGNOZA.obecnoscRodzica}</dd>
            </div>
            <div className={s.factRow}>
              <dt>Jak się umówić</dt>
              <dd>{DIAGNOZA.jakUmowic}</dd>
            </div>
          </dl>
        </section>

        {/* ---------------- TERAPEUTA ---------------- */}
        <section className={page.section} id="therapist">
          <h2>Kto przeprowadza badanie?</h2>
          <AuthorBox />
          <CtaRow text="Masz pytanie przed umówieniem wizyty? Chętnie na nie odpowiem." location="po-terapeucie" />
        </section>

        {/* ---------------- CENA ---------------- */}
        <section className={page.section} id="price">
          <h2>Ile kosztuje diagnoza?</h2>
          <div className={s.priceBox}>
            <span className={s.priceTag}>{cenaTekst(CENNIK.diagnoza)}</span>
            <span className={s.priceUnit}>za {CENNIK.diagnoza.jednostka}</span>
            <p style={{ marginTop: '1rem' }}>
              Cena obejmuje wywiad, badanie dziecka, omówienie wyników oraz pisemną opinię z
              zaleceniami. Nie ma dodatkowej opłaty za przygotowanie dokumentu.
            </p>
            <p style={{ margin: 0 }}>
              <a href="/cennik/">Zobacz pełny cennik diagnozy i terapii</a> - znajdziesz tam także
              koszt konsultacji, zajęć terapeutycznych oraz zasady odwoływania wizyt.
            </p>
          </div>
        </section>

        {/* ---------------- OPINIE ---------------- */}
        <section className={page.section} id="reviews">
          <h2>Co mówią rodzice?</h2>
          <div className={s.quotes}>
            {OPINIE.slice(0, 4).map((o) => (
              <figure className={s.quote} key={o.id}>
                <blockquote>{o.review}</blockquote>
                <figcaption>
                  {o.name} • {o.tag}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* ---------------- FAQ ---------------- */}
        <section className={page.section} id="faq">
          <h2>Najczęstsze pytania rodziców</h2>
          <div className={page.faqList}>
            {FAQ.map((f, i) => (
              <details className={page.faqItem} key={f.q}>
                <summary className={page.question}>
                  <span className={page.qIndex}>{String(i + 1).padStart(2, '0')}</span>
                  {f.q}
                </summary>
                <div className={page.answer}>
                  <p>{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* ---------------- LOKALIZACJA ---------------- */}
        <section className={page.section} id="location">
          <h2>Diagnoza sensoryczna w centrum Olsztyna</h2>
          <div className={s.localGrid}>
            <div>
              <address style={{ fontStyle: 'normal', marginBottom: '1rem' }}>
                <strong>{FIRMA.nazwa}</strong>
                <br />
                {ADRES_PELNY}
              </address>
              <ul className={page.list}>
                <li>{DOJAZD.dzielnica}</li>
                <li>{DOJAZD.komunikacja}</li>
                <li>{DOJAZD.parking}</li>
                <li>{DOJAZD.wejscie}</li>
                <li>{DOJAZD.dostepnosc}</li>
                <li>{DOJAZD.obszar}</li>
              </ul>
              <div className={page.contactRow}>
                <a className={page.contactLink} href={MAPS_NAV} target="_blank" rel="noreferrer">
                  Wyznacz trasę
                </a>
                <a className={page.contactLink} href={MAPS_LINK} target="_blank" rel="noreferrer">
                  Otwórz w Google Maps
                </a>
              </div>
              <p className={s.disclaimer}>Godziny przyjęć: {FIRMA.godzinyTekst}.</p>
            </div>
            <div className={page.mapBox}>
              <iframe
                title={`Mapa dojazdu do gabinetu Sensorini, ${ADRES_PELNY}`}
                src={MAPS_EMBED}
                className={page.mapFrame}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>

        <p className={s.disclaimer}>
          Treść ma charakter edukacyjny i nie zastępuje indywidualnej diagnozy specjalistycznej.
        </p>
      </div>

      {/* ---------------- KONTAKT KONCOWY ---------------- */}
      <section className={page.ctaBand} id="final-contact">
        <div className={page.ctaInner}>
          <h2>Umów diagnozę integracji sensorycznej</h2>
          <p>
            Zadzwoń, napisz albo wypełnij kwestionariusz sensomotoryczny (wypełnia go rodzic) -
            odezwę się i wspólnie ustalimy termin w gabinecie przy {FIRMA.ulica} w Olsztynie.
          </p>
          <div className={page.ctaBtns}>
            <a
              href="/kwestionariusz-sensomotoryczny/"
              className={page.btnPrimary}
              data-cta="booking"
              data-cta-location="final"
            >
              Wypełnij kwestionariusz o dziecku
            </a>
            <a href={`tel:${FIRMA.telefonE164}`} className={page.btnGhost}>
              Zadzwoń: {FIRMA.telefon}
            </a>
            <a href={`mailto:${FIRMA.email}`} className={page.btnGhost}>
              Napisz wiadomość
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
